import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthAPI } from '../services/api';
import { otpValidation } from '../schemas/validation';
import { LoadingScreen } from './LoadingScreen';
import './AuthForms.css';

const twoFactorSetupSchema = z.object({
  otp_code: otpValidation,
});

type TwoFactorSetupData = z.infer<typeof twoFactorSetupSchema>;

interface TwoFactorSetupProps {
  userEmail: string;
  onSetupComplete: () => void;
  onCancel?: () => void;
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ 
  userEmail, 
  onSetupComplete, 
  onCancel 
}) => {
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [setupMessage, setSetupMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TwoFactorSetupData>({
    resolver: zodResolver(twoFactorSetupSchema),
  });

  const handleSetup2FA = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await AuthAPI.setup2FA();
      setSetupMessage(response.message);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || '2FA setup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (data: TwoFactorSetupData) => {
    try {
      setLoading(true);
      setError('');
      
      await AuthAPI.enable2FA(data.otp_code);
      onSetupComplete();
    } catch (err: any) {
      setError(err.message || '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'setup') {
    return (
      <div className="auth-form-container">
        <div className="auth-form">
          <h2>Set Up Two-Factor Authentication</h2>
          <p className="info-text">
            For security, you must enable two-factor authentication before you can access your account.
            We'll send a verification code to <strong>{userEmail}</strong>.
          </p>
          
          {error && <div className="error-message">{error}</div>}
          
          <div style={{ 
            padding: '1rem', 
            background: 'var(--primary-light)', 
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid var(--primary)'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--primary)' }}>
              🔒 Why 2FA is Required:
            </h4>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '1.5rem', 
              color: 'var(--text-secondary)',
              fontSize: '0.9rem'
            }}>
              <li>Protects your account from unauthorized access</li>
              <li>Required by company security policy</li>
              <li>Uses email-based verification codes</li>
              <li>Cannot be disabled once enabled</li>
            </ul>
          </div>

          <div className="form-buttons">
            {onCancel && (
              <button 
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="btn btn-outline-secondary"
              >
                Cancel
              </button>
            )}
            <button 
              onClick={handleSetup2FA}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Setting up...' : 'Send Verification Code'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form-container">
      <form onSubmit={handleSubmit(handleEnable2FA)} className="auth-form">
        <h2>Verify Your Email</h2>
        <p className="info-text">
          {setupMessage || `We've sent a 6-digit verification code to ${userEmail}. Enter it below to complete 2FA setup.`}
        </p>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-group">
          <label htmlFor="otp_code">Verification Code</label>
          <input
            id="otp_code"
            type="text"
            {...register('otp_code')}
            className={errors.otp_code ? 'error' : ''}
            placeholder="Enter 6-digit code"
            maxLength={6}
            disabled={loading}
            autoComplete="one-time-code"
            autoFocus
          />
          {errors.otp_code && <span className="field-error">{errors.otp_code.message}</span>}
        </div>

        <div className="form-buttons">
          <button 
            type="button"
            onClick={() => {
              setStep('setup');
              setError('');
              reset();
            }}
            disabled={loading}
            className="btn btn-outline-secondary"
          >
            Back
          </button>
          <button 
            type="submit" 
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Verifying...' : 'Enable 2FA'}
          </button>
        </div>

        <div style={{ 
          marginTop: '1rem', 
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--text-muted)'
        }}>
          <p>Didn't receive the code?</p>
          <button
            type="button"
            onClick={handleSetup2FA}
            disabled={loading}
            className="btn btn-ghost"
          >
            Resend verification code
          </button>
        </div>
      </form>
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading} message="Setting up two-factor authentication..." />
    </div>
  );
};